import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import {
  Alert,
  Avatar,
  Button,
  Capacity,
  Card,
  Input,
  StatusBadge,
} from '@rallyshift/design-system'
import api from '../../api/client'
import { CAPACITY_LABELS, statusLabel } from '../../utils/status'
import { formatDayDateToLocal, formatTimeRangeToLocal } from '../../utils/timezone'

interface Assignment {
  id: string
  volunteer_id: string
  status: string
  first_name?: string
  last_name?: string
  email?: string
}

interface Candidate {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
  skills: string[] | null
  role: string
}

function fullName(p: { first_name?: string | null; last_name?: string | null; email?: string }) {
  return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || p.email || 'Onbekend'
}

/**
 * Matching and assignment for one shift (design screen 1g). Volunteers who lack a
 * required skill are not hidden, only marked and de-emphasised: the organiser knows
 * their people better than the skills list does.
 */
const MatchingPage: React.FC = () => {
  const { shiftId } = useParams<{ shiftId: string }>()

  const [shift, setShift] = useState<any>(null)
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!shiftId) return
    try {
      const [s, a, roster] = await Promise.all([
        api.shifts.get(shiftId),
        api.shifts.getAssignments(shiftId),
        api.volunteerMetadata.list(),
      ])
      setShift(s)
      setAssignments((a || []).filter((x: Assignment) => x.status !== 'cancelled'))
      setCandidates(roster || [])
    } catch (e: any) {
      setError(e.message || 'Deze dienst kon niet worden geladen.')
    } finally {
      setLoading(false)
    }
  }, [shiftId])

  useEffect(() => {
    load()
  }, [load])

  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => a.volunteer_id)),
    [assignments],
  )

  const required: string[] = useMemo(() => shift?.required_skills ?? [], [shift])

  const available = useMemo(() => {
    const term = search.trim().toLowerCase()
    return candidates
      .filter((c) => !assignedIds.has(c.id) && c.role !== 'user')
      .filter((c) => !term || fullName(c).toLowerCase().includes(term) || c.email.toLowerCase().includes(term))
      .map((c) => ({
        ...c,
        missing: required.filter((skill) => !(c.skills ?? []).includes(skill)),
      }))
      // Best matches first; a mismatch is still assignable, just further down.
      .sort((a, b) => a.missing.length - b.missing.length)
  }, [candidates, assignedIds, search, required])

  const assign = async (volunteerId: string) => {
    if (!shiftId) return
    setBusyId(volunteerId)
    try {
      await api.shifts.upsertAssignment(shiftId, volunteerId, 'assigned')
      await load()
    } catch (e: any) {
      setError(e.message || 'Toewijzen is niet gelukt.')
    } finally {
      setBusyId(null)
    }
  }

  const unassign = async (assignment: Assignment) => {
    setBusyId(assignment.volunteer_id)
    try {
      await api.shifts.updateAssignment(assignment.id, { status: 'cancelled' })
      await load()
    } catch (e: any) {
      setError(e.message || 'Verwijderen is niet gelukt.')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
      </div>
    )
  }

  if (!shift) {
    return <Alert variant="danger">{error || 'Deze dienst bestaat niet.'}</Alert>
  }

  const open = Math.max(shift.max_volunteers - assignments.length, 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-display-lg text-text-primary">
          Toewijzen — {shift.title}
        </h1>
        <p className="mt-2 text-md text-text-secondary">
          {formatDayDateToLocal(shift.start_time, 'EEE d MMM')} ·{' '}
          {formatTimeRangeToLocal(shift.start_time, shift.end_time)} ·{' '}
          {open > 0 ? `nog ${open} plek${open === 1 ? '' : 'ken'}` : 'vol'}
          {required.length > 0 && ` · vereist: ${required.join(', ')}`}
        </p>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title={`Toegewezen (${assignments.length})`}>
          <div className="mb-4">
            <Capacity
              value={assignments.length}
              max={shift.max_volunteers}
              stateLabels={CAPACITY_LABELS}
            />
          </div>

          {assignments.length === 0 ? (
            <p className="text-sm text-text-muted">Nog niemand toegewezen.</p>
          ) : (
            <ul className="divide-y divide-border">
              {assignments.map((a) => (
                <li key={a.id} className="flex items-center gap-3 py-3">
                  <Avatar name={fullName(a)} size={30} />
                  <span className="min-w-0 flex-1 truncate text-md text-text-primary">
                    {fullName(a)}
                  </span>
                  <StatusBadge
                    dimension="assignment"
                    status={a.status}
                    size="sm"
                    label={statusLabel('assignment', a.status)}
                  />
                  <button
                    type="button"
                    onClick={() => unassign(a)}
                    disabled={busyId === a.volunteer_id}
                    aria-label={`Haal ${fullName(a)} van deze dienst`}
                    className="min-h-touch-min px-2 text-text-muted hover:text-status-danger-text"
                  >
                    {'\u2715'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Beschikbaar">
          <div className="relative mb-4">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-text-muted" />
            </div>
            <Input
              className="pl-9"
              placeholder="Zoek…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {available.length === 0 ? (
            <p className="text-sm text-text-muted">Geen vrijwilligers gevonden.</p>
          ) : (
            <ul className="divide-y divide-border">
              {available.map((c) => {
                const mismatch = c.missing.length > 0
                return (
                  <li
                    key={c.id}
                    className={`flex items-center gap-3 py-3 ${mismatch ? 'opacity-70' : ''}`}
                  >
                    <Avatar name={fullName(c)} size={30} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-md text-text-primary">{fullName(c)}</p>
                      <p
                        className={`truncate text-[13px] ${
                          mismatch ? 'text-status-warning-text' : 'text-status-success-text'
                        }`}
                      >
                        {mismatch
                          ? `\u26A0 geen ${c.missing.join(', ')}-vaardigheid`
                          : required.length > 0
                            ? `\u2713 heeft ${required.join(', ')}`
                            : `\u2713 beschikbaar`}
                      </p>
                    </div>
                    <Button
                      variant={mismatch ? 'secondary' : 'primary'}
                      loading={busyId === c.id}
                      onClick={() => assign(c.id)}
                    >
                      {mismatch ? 'Toch toewijzen' : 'Wijs toe'}
                    </Button>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}

export default MatchingPage
